import { currentSupportersQuery } from "@/api/reporting";
import { DataTable } from "@/components/data-table/DataTable";
import { DataTableBody } from "@/components/data-table/DataTableBody";
import { PointsAmount } from "@/components/PointsAmount";
import { Button } from "@/components/ui/Button";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { formatTokensAsPoints, TokenCurrencyCode } from "@/modules/money-fns";
import { CurrentSupporterSummary } from "@givenwell/management-api";
import { Box, Checkbox, Flex, Label, Span, Text } from "@givenwell/components";
import { DownloadCircleIcon } from "@givenwell/core-solid-icons";
import { colors } from "@givenwell/theme";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { boolean, fallback, object, optional } from "valibot";

export const Route = createFileRoute("/admin/reports/$countryCode/current-supporters")({
  component: CurrentSupportersReportPage,
  validateSearch: object({
    // Kept in the URL so the view survives a refresh and can be shared.
    allCountries: fallback(optional(boolean(), false), false),
  }),
  beforeLoad: () => ({
    breadcrumb: "Current Supporters",
  }),
});

const formatFee = (amount: number, currency: string | null | undefined) =>
  `${amount.toFixed(2)}${currency ? ` ${currency}` : ""}`;

/**
 * Whether the row's discount actually changes what the supporter pays. A discount row can resolve to
 * the same figure as the standing rate - one supporter holds a 0.00 discount against a 0.00 base - and
 * such a discount is worth nothing to show: striking a figure out against an identical one reads as a
 * discount while conveying nothing, and its end date is a date on which nothing will change.
 *
 * Both the fee cell and the Discount Until column gate on this, so the two always agree.
 */
const hasEffectiveDiscount = (row: CurrentSupporterSummary) =>
  row.member_fee_amount != null &&
  row.discounted_fee_amount != null &&
  row.discounted_fee_amount !== row.member_fee_amount;

/**
 * The same question for the base fee. Kept separate from the member-fee check because a discount
 * states both halves independently: one can change while the other does not.
 */
const hasEffectiveBaseDiscount = (row: CurrentSupporterSummary) =>
  row.base_fee_amount != null &&
  row.discounted_base_fee_amount != null &&
  row.discounted_base_fee_amount !== row.base_fee_amount;

/**
 * The per-supporter base fee, charged once a month on the home-currency invoice. Shown the same way
 * as the member fee, including the struck-through standing rate when a discount changes it.
 */
function BaseFeeCell({ row }: { row: CurrentSupporterSummary }) {
  if (row.base_fee_amount == null) {
    return <Span css={{ color: colors.text3 }}>—</Span>;
  }

  const base = formatFee(row.base_fee_amount, row.fee_currency_code);

  if (!hasEffectiveBaseDiscount(row)) {
    return <Span>{base}</Span>;
  }

  return (
    <Flex css={{ gap: 6, justifyContent: "flex-end", alignItems: "baseline" }}>
      <Span css={{ textDecoration: "line-through", color: colors.text3 }}>{base}</Span>
      <Span css={{ color: colors.green700, fontWeight: 500 }}>
        {formatFee(row.discounted_base_fee_amount!, row.fee_currency_code)}
      </Span>
    </Flex>
  );
}

/**
 * The platform fee per member. A supporter on an effective discount shows the standing rate struck
 * through next to the rate they actually pay, so it is obvious both that they are discounted and what
 * they would otherwise be charged. Most discounts in play take the fee to zero, so the struck-through
 * figure is the only thing showing what the supporter would be billed without it.
 *
 * A consolidated supporter is invoiced once in its home currency for every country, so the API puts the
 * fee on the home-country row alone and the other rows read "—".
 */
function PlatformFeeCell({ row }: { row: CurrentSupporterSummary }) {
  if (row.member_fee_amount == null) {
    return <Span css={{ color: colors.text3 }}>—</Span>;
  }

  const base = formatFee(row.member_fee_amount, row.fee_currency_code);

  if (!hasEffectiveDiscount(row)) {
    return <Span>{base}</Span>;
  }

  return (
    <Flex css={{ gap: 6, justifyContent: "flex-end", alignItems: "baseline" }}>
      <Span css={{ textDecoration: "line-through", color: colors.text3 }}>{base}</Span>
      <Span css={{ color: colors.green700, fontWeight: 500 }}>
        {formatFee(row.discounted_fee_amount!, row.fee_currency_code)}
      </Span>
    </Flex>
  );
}

/**
 * The allowance figure as the table draws it, via the same token-to-points conversion PointsAmount
 * uses. That conversion reads the token values out of the query cache and throws when they are
 * missing, so fall back to the raw token count rather than failing the whole export.
 */
const formatAllowanceForCsv = (row: CurrentSupporterSummary) => {
  try {
    return formatTokensAsPoints({
      amount: row.avg_monthly_tokens,
      // The API types this as a plain string; PointsAmount narrows it the same way to format the cell.
      tokenCurrencyCode: row.token_currency_code as TokenCurrencyCode,
    });
  } catch {
    return row.avg_monthly_tokens.toString();
  }
};

/**
 * One CSV row per table row, with the columns the table is currently showing — the Country column
 * only appears in the all-countries view, so the export mirrors that.
 *
 * Fee columns are split into what the supporter would be charged and what they actually pay, rather
 * than reproducing the struck-through cell as one string: a spreadsheet wants two numbers. Both
 * discount columns are gated on the same hasEffectiveDiscount/hasEffectiveBaseDiscount checks the
 * cells use, so a discount that changes nothing stays out of the export as it stays off the screen.
 * Dates go out as the raw ISO values the API returns, which sort and parse in a spreadsheet; the
 * table's toLocaleDateString form does neither.
 */
function buildCsv(rows: CurrentSupporterSummary[], allCountries: boolean) {
  const header = [
    "Customer",
    "Givenwell",
    ...(allCountries ? ["Country"] : []),
    "First Allowances",
    "Active Members",
    "Plan",
    "Custom Pricing",
    "Fee Currency",
    "Base Fee",
    "Discounted Base Fee",
    "Platform Fee / Member",
    "Discounted Platform Fee / Member",
    "Discount Until",
    "Avg Monthly Allowance",
    "EAP",
  ];

  const body = rows.map(row => [
    row.name,
    row.is_givenwell_supporter ? "Yes" : "No",
    ...(allCountries ? [row.country_code] : []),
    row.first_allowance_date ?? "",
    row.active_member_count.toString(),
    row.plan_name ?? "",
    row.has_custom_pricing ? "Yes" : "No",
    row.fee_currency_code ?? "",
    row.base_fee_amount?.toFixed(2) ?? "",
    hasEffectiveBaseDiscount(row) ? row.discounted_base_fee_amount!.toFixed(2) : "",
    row.member_fee_amount?.toFixed(2) ?? "",
    hasEffectiveDiscount(row) ? row.discounted_fee_amount!.toFixed(2) : "",
    hasEffectiveDiscount(row) ? (row.discount_ends_on ?? "") : "",
    formatAllowanceForCsv(row),
    row.running_eap ? "Yes" : "No",
  ]);

  return [header, ...body].map(row => row.map(csvEscape).join(",")).join("\r\n");
}

function csvEscape(value: string) {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function DownloadCurrentSupportersCsvButton({
  rows,
  allCountries,
  countryCode,
}: {
  rows: CurrentSupporterSummary[];
  allCountries: boolean;
  countryCode: string;
}) {
  const downloadCsv = () => {
    const blob = new Blob([buildCsv(rows, allCountries)], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    a.download = `current-supporters-${allCountries ? "all" : countryCode.toLowerCase()}.csv`;
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(url);
    a.remove();
  };

  return (
    <Button
      variant="tonal"
      size="sm"
      css={{ fontScale: "sm", color: colors.text, weight: 500 }}
      onClick={downloadCsv}
      disabled={rows.length === 0}
      leftIcon={<DownloadCircleIcon />}
    >
      <span>Export CSV</span>
    </Button>
  );
}

function CurrentSupportersReportPage() {
  const { countryCode } = Route.useParams();
  const { allCountries } = Route.useSearch();
  const navigate = useNavigate();

  const currentSupporters = useQuery(currentSupportersQuery(allCountries ? null : countryCode));

  const rows = currentSupporters.data ?? [];

  return (
    <PageWrapper
      title="Current Supporters"
      rightContent={
        <DownloadCurrentSupportersCsvButton rows={rows} allCountries={allCountries} countryCode={countryCode} />
      }
    >
      <Flex css={{ gap: 8, alignItems: "center", mb: 16 }}>
        <Checkbox
          checked={allCountries}
          onCheckedChange={checked =>
            navigate({ to: ".", params: { countryCode }, search: { allCountries: checked === true } })
          }
        />
        <Label css={{ fontSize: 14 }}>All countries</Label>
        <Text css={{ fontScale: "2xs", color: colors.text3 }}>
          {allCountries
            ? "One row per country a supporter has members in, each in that country's own currency."
            : `Supporters with members in ${countryCode}.`}
        </Text>
      </Flex>

      <DataTable>
        <DataTableBody
          data={rows}
          status={currentSupporters.status}
          rounded="full"
          columns={[
            // NOTE: `formatter` is deliberately omitted on every column with a function `cell`.
            // DataTableBody applies a formatter by running Number()/new Date() over whatever `cell`
            // returned and replacing it, so pairing one with a rendered node or a composed string
            // yields "NaN"/"Invalid Date". Each cell below formats its own value instead.
            // Alignment goes through `css`, not `textAlign`: DataTableBody applies `textAlign` to the
            // header only, so a cell picks it up solely as a side effect of a `formatter`. `css` is
            // spread into both the header and the body cell, so the two always agree.
            // Sortable columns carry an explicit columnKey: getColumnKey falls back to the header for a
            // function cell, so naming the key keeps the sort state stable if a header is ever reworded.
            // The numeric columns need sortingFn "number" - the default is "text", which would order
            // 1000 before 200.
            {
              header: "Customer",
              columnKey: "name",
              cell: d => (
                <Flex css={{ gap: 6, alignItems: "baseline" }}>
                  <Span>{d.name}</Span>
                  {/* Most obs reports exclude these; this one includes them, so mark them. */}
                  {d.is_givenwell_supporter && (
                    <Span css={{ fontScale: "2xs", color: colors.text3 }}>Givenwell</Span>
                  )}
                </Flex>
              ),
              getValue: d => d.name,
              enableSorting: true,
            },
            ...(allCountries
              ? [
                  {
                    header: "Country",
                    columnKey: "country",
                    cell: (d: CurrentSupporterSummary) => d.country_code,
                    getValue: (d: CurrentSupporterSummary) => d.country_code,
                    enableSorting: true,
                    css: { textAlign: "center" as const },
                    width: "90px",
                  },
                ]
              : []),
            {
              header: "First Allowances",
              columnKey: "firstAllowance",
              // Null until a supporter's first allowance batch has actually been processed.
              cell: d =>
                d.first_allowance_date ? new Date(d.first_allowance_date).toLocaleDateString() : "—",
              // The raw ISO date sorts correctly as text; rows with no allowances yet sort to the top
              // ascending, which keeps them together rather than scattered.
              getValue: d => d.first_allowance_date ?? "",
              enableSorting: true,
              css: { textAlign: "right" },
              width: "140px",
            },
            {
              header: "Active Members",
              columnKey: "activeMembers",
              cell: "active_member_count",
              formatter: "number",
              getValue: d => d.active_member_count,
              enableSorting: true,
              sortingFn: "number",
              css: { textAlign: "right" },
              width: "130px",
            },
            {
              header: "Plan",
              columnKey: "plan",
              cell: d => (
                <Flex css={{ gap: 6, alignItems: "baseline" }}>
                  <Span>{d.plan_name ?? "—"}</Span>
                  {/* Their rates deviate from the plan, so the plan name alone would mislead. */}
                  {d.has_custom_pricing && (
                    <Span css={{ fontScale: "2xs", color: colors.text3 }}>custom</Span>
                  )}
                </Flex>
              ),
              getValue: d => d.plan_name ?? "",
              enableSorting: true,
              width: "140px",
            },
            {
              header: "Base Fee",
              columnKey: "baseFee",
              cell: d => <BaseFeeCell row={d} />,
              // Sort on what is actually charged, as the member fee column does.
              getValue: d => d.discounted_base_fee_amount ?? d.base_fee_amount ?? -1,
              enableSorting: true,
              sortingFn: "number",
              css: { textAlign: "right" },
              width: "150px",
            },
            {
              header: "Platform Fee / Member",
              columnKey: "platformFee",
              cell: d => <PlatformFeeCell row={d} />,
              // Sort on what they actually pay, which is the discounted rate when one applies. A row
              // with no fee at all sorts below every real fee rather than alongside a genuine 0.00.
              getValue: d => d.discounted_fee_amount ?? d.member_fee_amount ?? -1,
              enableSorting: true,
              sortingFn: "number",
              css: { textAlign: "right" },
              width: "200px",
            },
            {
              header: "Discount Until",
              columnKey: "discountUntil",
              // Gated on hasEffectiveDiscount, not just on the date being present: a discount that does
              // not change the price has an end date, but nothing happens when it passes.
              cell: d =>
                d.discount_ends_on && hasEffectiveDiscount(d) ? (
                  <Span css={{ color: colors.green700 }}>
                    {new Date(d.discount_ends_on).toLocaleDateString()}
                  </Span>
                ) : (
                  <Span css={{ color: colors.text3 }}>—</Span>
                ),
              // Raw ISO date, so sorting descending groups the live discounts together at the top.
              getValue: d => (hasEffectiveDiscount(d) ? (d.discount_ends_on ?? "") : ""),
              enableSorting: true,
              css: { textAlign: "right" },
              width: "140px",
            },
            {
              header: "Avg Monthly Allowance",
              columnKey: "avgMonthlyAllowance",
              cell: d => <PointsAmount amount={d.avg_monthly_tokens} tokenCurrency={d.token_currency_code} />,
              getValue: d => d.avg_monthly_tokens,
              enableSorting: true,
              sortingFn: "number",
              css: { textAlign: "right" },
              width: "180px",
            },
            {
              header: "EAP",
              columnKey: "eap",
              cell: d => (d.running_eap ? "Yes" : "No"),
              // Numeric so descending puts the EAP supporters together at the top.
              getValue: d => (d.running_eap ? 1 : 0),
              enableSorting: true,
              sortingFn: "number",
              css: { textAlign: "center" },
              width: "70px",
            },
          ]}
          dataKey={d => `${d.supporter_id}:${d.country_code}`}
        />
      </DataTable>
      <Box css={{ mt: 12 }}>
        <Text css={{ fontScale: "2xs", color: colors.text3 }}>
          Allowance figures are a committed monthly run-rate (each recurring rule's amount divided by its
          frequency), not a historical average. Fees are what the supporter is charged: their plan's
          rates, or their own where these differ, marked &ldquo;custom&rdquo;. The base fee is charged
          once a month per supporter, on its home-country row only, as is the whole platform fee for a
          supporter invoiced once for all countries.
        </Text>
      </Box>
    </PageWrapper>
  );
}
